import { defineStore } from 'pinia';
import type {
  FullDBUser,
  MembershipWithUser
} from '~~/server/services/service.types';
import type { Plan } from '~~/server/schemas/dbTypes';
import type { Membership } from '~~/server/schemas/types';
import type { AccountMini } from '~~/server/middleware/authContext';
import type { AccountMember, AccountMemberEx } from '~~/server/services/account.service';
import type { GetDBUserResult } from '~~/types/application';

/*
This store manages User and Account state including the ActiveAccount
It is used in the Account administration page and the header due to it's account switching features.

Note) Other pages don't need this state as the dbUser and activeAccount are available on the TRPC Context 
so that other routers can use them to filter results to the active user and account transparently.

[State Map]

(supabase) user -> dbUser                       <-- this.dbUser
                     |
                     |
  membership-----membership------membership*    <-- this.dbUser.memberships (*this.isMembershipActive)
       |             |               |
    account       account         account**    <--**(id=this.activeAccount)
                                     |
                   membership-----membership------membership     <-- this.activeAccountMembers
                       |             |               |
                    account       account        account*
*/
export const useAccountStore = defineStore('account', () => {
  const user = ref<GetDBUserResult['user'] | null>(null);
  const plan = ref<Plan | null>(null);
  const activeAccount =ref<GetDBUserResult['account'] | null>(null);
  const roles = ref<string[]>([]);

  const activeAccountMembers = ref<AccountMemberEx[]>([]);
  const isMembershipActive = ref(false);

  const init = async () => {
    const { $trpc } = useNuxtApp();
    if (!user.value) {
      // const planPromise = $trpc.plans.getCurrentPlan.query();
      // const dbUserPromise = $trpc.auth.getDBUser.query();
      const { user: _user, roles: _roles, account: _account } = await $trpc.auth.getDBUser.query() as GetDBUserResult;
      const { plan: _plan}      = await $trpc.plans.getCurrentPlan.query();

      if (_user?.id) {
        user.value = _user;
        //isMembershipActive.value =  user?.value?.membership as Membership;
        activeAccount.value = _account
        roles.value = _roles
        isMembershipActive.value = (_user.pending == 1)
      }
      if (_plan) {
        plan.value = _plan;
      }
    }
    // if (!activeAccount.value) {
    //   const { activeAccount: _activeAccount } =
    //     await $trpc.account.getactiveAccount.query();
    //   if (_activeAccount) {
    //     activeAccount.value = _activeAccount;
    //   }
    // }
  };

  const signout = () => {
    user.value = null;
    plan.value = null;
    activeAccountMembers.value = [];
  };

  const getActiveAccountMembers = async () => {
    const { $trpc } = useNuxtApp();
    const result = await $trpc.account.getAccountMembers.query();
    if (result?.memberships) {
      activeAccountMembers.value = result.memberships;
    }
  };

  const acceptPendingMembership = async (membership_id: string) => {
    const { $trpc } = useNuxtApp();
    const { data: membership } =
      await $trpc.account.acceptPendingMembership.useQuery({
        membership_id
      });

    // if (membership.value && membership.value.membership?.pending === false) {
    //   for (const m of activeAccountMembers.value) {
    //     if (m.id === membership_id) {
    //       m.pending = 1;
    //     }
    //   }
    // }
  };

  const rejectPendingMembership = async (membership_id: string) => {
    const { $trpc } = useNuxtApp();
    const { data: membership } =
      await $trpc.account.rejectPendingMembership.useQuery({
        membership_id
      });

    if (membership.value) {
      activeAccountMembers.value = activeAccountMembers.value.filter(
        (m: AccountMemberEx) => m.id !== membership_id);
    }
  };

  const deleteMembership = async (membership_id: string) => {
    const { $trpc } = useNuxtApp();
    const { data: membership } =
      await $trpc.account.deleteMembership.useQuery({ membership_id });

    if (membership.value) {
      activeAccountMembers.value = activeAccountMembers.value.filter(
        (m: AccountMemberEx) => m.id !== membership_id
      );
    }
  };

  const rotateJoinPassword = async () => {
    const { $trpc } = useNuxtApp();
    const { account } = await $trpc.account.rotateJoinPassword.mutate();
    // if (account && isMembershipActive) {
    //   isMembershipActive.activeAccount.id = account.id;
    // }
  };

  const joinUserToAccountPending = async (account_id: string) => {
    if (!user.value) {
      return;
    }
    const { $trpc } = useNuxtApp();
    const { membership } =
      await $trpc.account.joinUserToAccountPending.mutate({
        account_id,
        user_id: user.value.id
      });
    if (membership && isMembershipActive) {
      activeAccountMembers.value = membership;
    }
  };

  // const changeUserAccessWithinAccount = async (
  //   user_id: number,
  //   access: ACCOUNT_ACCESS
  // ) => {
  //   const { $trpc } = useNuxtApp();
  //   const { membership } =
  //     await $trpc.account.changeUserAccessWithinAccount.mutate({
  //       user_id,
  //       access
  //     });
  //   if (membership) {
  //     for (const m of activeAccountMembers.value) {
  //       if (m.membership.id === membership.id) {
  //         m.access = membership.access;
  //       }
  //     }
  //   }
  // };

  // const claimOwnershipOfAccount = async () => {
  //   const { $trpc } = useNuxtApp();
  //   const { memberships } =
  //     await $trpc.account.claimOwnershipOfAccount.mutate();
  //   if (memberships) {
  //     activeAccountMembers.value = memberships;
  //     isMembershipActive.value!.access = ACCOUNT_ACCESS.OWNER;
  //   }
  // };

  const changeAccountName = async (name: string) => {
    const { $trpc } = useNuxtApp();
    const { account } = await $trpc.account.changeAccountName.mutate({
      name
    });
    if (account && isMembershipActive) {
      account.value.id = account.id;
    }
  }

  const hasAnyOfTheseRoles = (roles2: string[]): boolean => {
    if (!roles.value ) {
      return false;
    }
    return roles.value.some(role => roles2.includes(role));
  };
  
  return {
    user,
    plan,
    roles,
    activeAccount,
    activeAccountMembers,
    isMembershipActive,
    init,
    signout,
    getActiveAccountMembers,
    hasAnyOfTheseRoles,
    // changeActiveAccount,
    changeAccountName,
    acceptPendingMembership,
    rejectPendingMembership,
    deleteMembership,
    rotateJoinPassword,
    joinUserToAccountPending,
    
  };
});
